import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, MapPin, Mail, ShieldCheck, Linkedin } from "lucide-react";
import { Reveal, MaskedLines, SectionLabel } from "@/components/Reveal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import NewsletterForm from "@/components/NewsletterForm";
import SchedulePicker from "@/components/SchedulePicker";
import { PRODUCTS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORG_TYPES = ["Startup", "Small Business", "Mid-Market", "Enterprise", "Public Sector", "Non-Profit", "Other"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", industry: "", interest: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [slot, setSlot] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/leads/demo`, form);
      setLeadId(data._id || data.id);
      setSubmitted(true);
      toast.success("Demo request received. Pick a slot below.");
    } catch {
      toast.error("Submission failed. Please check required fields.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full border border-zinc-900/20 bg-transparent px-4 py-3.5 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-cyan-500 focus:outline-none transition-colors duration-200";

  return (
    <main data-testid="contact-page" className="pt-32">
      <section className="grid-bg border-b border-zinc-900/10 pb-16">
        <div className="mx-auto max-w-7xl px-6">
          <SectionLabel>Contact</SectionLabel>
          <MaskedLines
            className="mt-6"
            lines={["Bring your hardest", <span key="a" className="text-cyan-600">workflow.</span>]}
            lineClassName="font-heading text-5xl font-bold tracking-tighter leading-[1.05] text-zinc-900 sm:text-6xl lg:text-7xl"
          />
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 py-24 lg:grid-cols-5">
        {/* DEMO FORM */}
        <div className="lg:col-span-3">
          <Reveal>
            <h2 className="font-heading text-2xl font-semibold text-zinc-900">Book a Demo</h2>
            <p className="mt-2 text-sm text-zinc-500">30 minutes with a platform engineer. No sales deck.</p>
          </Reveal>
          {submitted ? (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-10">
              <div data-testid="demo-form-success" className="border border-cyan-500/40 bg-cyan-500/5 p-8">
                <Check size={24} className="text-cyan-600" />
                <h3 className="mt-4 font-heading text-2xl font-semibold text-zinc-900">{slot ? "Demo booked." : "Request received."}</h3>
                <p className="mt-3 text-sm leading-relaxed text-zinc-600">
                  {slot ? (
                    <>Your walkthrough is confirmed for <span className="font-mono text-cyan-600">{slot}</span>. A confirmation email is on its way to <span className="text-cyan-600">{form.email}</span>.</>
                  ) : (
                    <>Confirmation sent to <span className="text-cyan-600">{form.email}</span>. Lock in a time below, or we'll propose slots within one business day.</>
                  )}
                </p>
              </div>
              {!slot && leadId && <SchedulePicker leadId={leadId} onScheduled={setSlot} />}
            </motion.div>
          ) : (
            <Reveal delay={0.1}>
              <form onSubmit={submit} data-testid="demo-form" className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
                <input data-testid="demo-name-input" required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} />
                <input data-testid="demo-email-input" required type="email" placeholder="Work email *" value={form.email} onChange={set("email")} className={inputCls} />
                <input data-testid="demo-company-input" required placeholder="Company *" value={form.company} onChange={set("company")} className={inputCls} />
                <input data-testid="demo-role-input" placeholder="Role / title" value={form.role} onChange={set("role")} className={inputCls} />
                <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                  <SelectTrigger data-testid="demo-industry-select" className="h-[50px] rounded-none border-zinc-900/20 bg-transparent font-normal text-zinc-900 data-[placeholder]:text-zinc-400">
                    <SelectValue placeholder="Organization type" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-zinc-900/20 bg-[#FFFFFF] text-zinc-900">
                    {ORG_TYPES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                  <SelectTrigger data-testid="demo-interest-select" className="h-[50px] rounded-none border-zinc-900/20 bg-transparent font-normal text-zinc-900 data-[placeholder]:text-zinc-400">
                    <SelectValue placeholder="Product of interest" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-zinc-900/20 bg-[#FFFFFF] text-zinc-900">
                    <SelectItem value="Platform Overview">Platform Overview</SelectItem>
                    {PRODUCTS.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <textarea data-testid="demo-message-input" rows={4} placeholder="What workflow should we solve in the demo?" value={form.message} onChange={set("message")} className={`${inputCls} sm:col-span-2 resize-none`} />
                <button data-testid="demo-submit-btn" type="submit" disabled={loading} className="btn-primary sm:col-span-2">
                  {loading ? "Submitting…" : <>Request Demo <ArrowUpRight size={16} /></>}
                </button>
              </form>
            </Reveal>
          )}
        </div>

        {/* SIDEBAR */}
        <div className="space-y-6 lg:col-span-2">
          <Reveal delay={0.15}>
            <div className="border border-zinc-900/10 bg-[#FFFFFF] p-8">
              <p className="label-mono">The Signal — Newsletter</p>
              <p className="mt-4 text-sm leading-relaxed text-zinc-600">Monthly analysis on enterprise AI, compliance, and the trust layer. Join operators from every kind of business.</p>
              <div className="mt-6"><NewsletterForm testId="contact-newsletter" /></div>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="border border-zinc-900/10 bg-[#FFFFFF] p-8">
              <p className="label-mono">Company</p>
              <div className="mt-6 space-y-4 text-sm text-zinc-600">
                <p className="flex items-center gap-3"><Mail size={15} className="text-cyan-600" /> admin@aintelligence.systems</p>
                <p className="flex items-center gap-3"><MapPin size={15} className="text-cyan-600" /> Atlanta / Remote-first</p>
                <a
                  data-testid="contact-linkedin-link"
                  href="https://www.linkedin.com/company/augmented-intelligence-systems/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 transition-colors hover:text-cyan-600"
                >
                  <Linkedin size={15} className="text-cyan-600" /> LinkedIn ↗
                </a>
                <p className="flex items-center gap-3"><ShieldCheck size={15} className="text-cyan-600" /> SOC 2 Type II · HIPAA-ready</p>
              </div>
              <div className="mt-8 border-t border-zinc-900/10 pt-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] leading-relaxed text-zinc-400">
                  response_time: &lt; 1 business day<br />
                  demo_length: 30 min<br />
                  nda_available: true
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
